import React from "react";
import LegalLayout from "../components/LegalLayout";

const PrivacyPage: React.FC = () => (
  <LegalLayout
    title="Privacy Statement"
    path="/privacy"
    description="How PABS collects, uses, and shares information across our food, grocery, pharmacy, and essentials delivery services, website, and applications."
    intro="How PABS collects, uses, and shares information when you use our delivery and errand services."
  >
        <p>
          This Privacy Statement explains how PABS collects, uses, and shares information when you use
          our food, grocery, pharmacy, and essentials delivery services, website, and applications.
        </p>

        <h2>Information we collect</h2>
        <p>
          We collect the following categories of personal information when you use PABS:
        </p>
        <ul>
          <li>
            <strong>Account information</strong>: Name, email, phone number, the Google or Apple account you sign in
            with, and profile details. For riders and merchants, ID photos submitted for verification and a profile photo.
          </li>
          <li>
            <strong>Delivery address</strong>: Pickup and drop-off locations for your orders and deliveries.
          </li>
          <li>
            <strong>Order history</strong>: Details of your orders, delivery schedules, notes, items, and service type.
          </li>
          <li>
            <strong>Location data</strong>: Your location when you set pickup and drop-off points and while a booking is
            active. Riders share their location continuously while online, for dispatch and live tracking.
          </li>
          <li>
            <strong>Device information</strong>: Device identifiers, notification tokens, operating system, app version, and
            device model used to access PABS.
          </li>
          <li>
            <strong>Payment data</strong>: Your payment method (Cash on Delivery or GCash), amounts, and the GCash receipts
            you upload. We never collect card numbers, bank logins, or e-wallet PINs.
          </li>
          <li>
            <strong>Log data</strong>: IP address, pages and features viewed, timestamps, and user interactions for security and troubleshooting.
          </li>
          <li>
            <strong>Messages</strong>: Chats with your rider and support tickets, including photos or files you attach.
          </li>
          <li>
            <strong>Applications</strong>: The name, contact details, town, and business details you send when applying
            to become a rider or to list your store, used only to review and follow up on your application.
          </li>
        </ul>

        <h2>Why we collect and use information</h2>
        <p>
          We use the information we collect for the following purposes:
        </p>
        <ul>
          <li><strong>Order fulfillment</strong>: Process, fulfill, and manage your orders and deliveries.</li>
          <li><strong>Delivery partner coordination</strong>: Match your request with available delivery partners and share necessary information to complete your delivery.</li>
          <li><strong>Communication</strong>: Communicate with you about your orders, account status, delivery updates, and important service notices.</li>
          <li><strong>Customer support</strong>: Respond to your inquiries, resolve issues, and provide technical support.</li>
          <li><strong>Service improvement</strong>: Analyze usage patterns to improve our service, develop new features, and optimize user experience.</li>
          <li><strong>Security and fraud prevention</strong>: Secure the platform, prevent abuse, investigate suspicious activity, and enforce our terms of service.</li>
          <li><strong>Legal compliance</strong>: Comply with applicable laws, regulations, and legal obligations.</li>
        </ul>

        <h2>Who we share information with</h2>
        <p>
          We share your information with the following categories of recipients:
        </p>
        <ul>
          <li><strong>Delivery partners and restaurants</strong>: We share delivery addresses, order details, and contact information with delivery partners and restaurants necessary to fulfill your order.</li>
          <li><strong>IT service providers</strong>: We share data with third-party service providers who assist us with hosting, cloud infrastructure, data backup, analytics, mapping services, payment processing, messaging, and other technical operations.</li>
          <li><strong>Legal compliance</strong>: We may share information if required by law, court order, or government request, or to protect the rights, safety, or property of PABS, our users, or the public.</li>
        </ul>

        <h2>Advertising in the app</h2>
        <p>
          The PABS app shows ads through Google AdMob, which may use your device's advertising identifier. You can reset
          or limit that identifier in your phone's settings. This website does not show ads.
        </p>

        <h2>Cookies and similar technologies</h2>
        <p>
          We use cookies and similar technologies to operate and secure the site and app, and this website uses Google
          Analytics to measure how it is used. For details, including how to opt out of analytics, see our{' '}
          <a href="/cookies">Cookies Notice</a>.
        </p>

        <h2>How long we retain your information</h2>
        <p>
          We retain your personal information for as long as necessary to:
        </p>
        <ul>
          <li>Provide and operate the PABS service.</li>
          <li>Comply with applicable legal and regulatory obligations.</li>
          <li>Resolve disputes and enforce our agreements.</li>
          <li>Support customer service and historical reference.</li>
        </ul>
        <p>
          Retention periods vary depending on the type of data and purpose. For example, we retain order and delivery records for
          the duration of your account and a reasonable period thereafter for support and legal purposes. Payment records
          are retained as required by law. You may request deletion of your data subject to legal obligations; see
          <a href="/privacy-requests"> Privacy Requests</a> for details.
        </p>

        <h2>Your rights</h2>
        <p>
          Depending on your location and applicable laws, you may have the following rights regarding your personal information:
        </p>
        <ul>
          <li><strong>Right to access</strong>: Request access to the personal information PABS holds about you.</li>
          <li><strong>Right to correct</strong>: Request correction or updating of inaccurate or incomplete personal information.</li>
          <li><strong>Right to erasure</strong>: Request deletion of your personal information, subject to legal retention obligations.</li>
          <li><strong>Right to object</strong>: Object to or restrict certain types of processing of your information (e.g., marketing communications).</li>
          <li><strong>Right to data portability</strong>: Request a copy of your personal information in a structured, commonly used, and machine-readable format.</li>
        </ul>
        <p>
          To exercise any of these rights, please submit a request to <a href="/privacy-requests">Privacy Requests</a> or contact
          us at <a href="mailto:privacy@pabs.online">privacy@pabs.online</a>. We will verify your identity and respond to your request
          within the timeframe required by applicable law.
        </p>

        <h2>Security</h2>
        <p>
          We use administrative, technical, and organizational safeguards designed to protect information. Learn more
          on our <a href="/security">Security</a> page.
        </p>

        <h2>Contact</h2>
        <p>
          Privacy questions: <a href="mailto:privacy@pabs.online">privacy@pabs.online</a>
        </p>

        <p className="legal-note">
          This page is provided for general informational purposes and may be updated from time to time.
        </p>
  </LegalLayout>
);

export default PrivacyPage;
